import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ampli } from '../../../ampli';
import { MainHead } from '../../../components/common/head/MainHead';
import { AnimateLoading } from '../../../components/common/loading/AnimateLoading';
import { PopupConfirmModal } from '../../../components/common/modal/popup/PopupConfirmModal';
import { browserStorageKeys } from '../../../constants/browserStorageKeys/browserStorageKeys.constant';
import { pageInfo } from '../../../constants/pageInfo/pageInfo.constant';
import { useGetProductByIdQuery } from '../../../hooks/api/product/queries/useGetProductById.query';
import { useLayout } from '../../../hooks/screen/useLayout';
import { useScrollY } from '../../../hooks/screen/useScrollY';
import { useCheckUserLoggedIn } from '../../../hooks/user/useCheckUserLoggedIn';
import { browserStorage } from '../../../utils/browserStorage.util';
import { postMessageToRN } from '../../../utils/RNCommunicator/postMessageToRN.util';
import { ProductDetailView } from '../../../views/Product/detailView/ProductDetailView/ProductDetailView';
import { InstallmentPossibleSection } from './components/InstallmentPossibleSection';
import { ProductDetailPageFooter } from './components/ProductDetailPageFooter';
import { RequestConsultButton } from './components/RequestConsultButton';
import { useQueryClient } from '@tanstack/react-query';
import { usePostUserActivityTracking } from '../../../hooks/api/user/mutations/usePostUserActivityTracking.mutation';
import { useGetUserProfile } from '../../../hooks/user/useGetUserProfile';

const MAIN_HEAD_HEIGHT = 48;
const INSTALLMENT_POSSIBLE_SECTION_HEIGHT = 36.8;

export const ProductDetailPage = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const { productId } = useParams();
  const { mutate: postUserActivityTracking } = usePostUserActivityTracking();
  const { userProfile } = useGetUserProfile();

  const [isAmpliEventCalled, setIsAmpliEventCalled] = useState<boolean>(false);

  const [isUserActivityUpdated, setIsUserActivityUpdated] = useState<boolean>(false);

  const { scrollY } = useScrollY();
  const { isUserLoggedIn } = useCheckUserLoggedIn();

  const { data: product, isLoading: isGetProductQueryLoading } = useGetProductByIdQuery({
    productId: productId!,
  });

  const isRequestConsultModalOpenTrigged = useMemo<boolean>(() => {
    if (window.pageYOffset > 2000) {
      return true;
    }

    return false;
  }, [window.pageYOffset, scrollY]);

  useEffect(() => {
    if (product) {
      if (product.status !== 'ONSALE' && product.status !== 'IN_CONTRACT') {
        navigate('/');
      }
    }
  }, [product, navigate]);

  useEffect(() => {
    if (product && !isAmpliEventCalled) {
      if (product.category === 'PLUVA') {
        ampli.마켓플루바매물상세페이지조회({
          '게시글 제목': product.title,
          '매물 코드': product.code,
        });
      } else {
        ampli.마켓파트너매물상세페이지조회({
          '게시글 제목': product.title,
          '매물 코드': product.code,
        });
      }

      setIsAmpliEventCalled(true);
    }
  }, [product, isAmpliEventCalled]);

  useEffect(() => {
    if (!isUserActivityUpdated) {
      postUserActivityTracking('productDetailPageVisitedCount', {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['user_activity_tracking', 'PDP'] });
        },
      });

      const visitedCount = browserStorage.getItem({
        storageType: 'localStorage',
        itemKey: browserStorageKeys.visitedCountPDP,
      });

      const newCount = visitedCount ? Number(visitedCount) + 1 : 1;
      browserStorage.setItem({
        storageType: 'localStorage',
        itemKey: browserStorageKeys.visitedCountPDP,
        value: newCount.toString(),
      });

      setIsUserActivityUpdated(true);
    }
  }, [postUserActivityTracking, isUserActivityUpdated, queryClient]);

  const isAdmin = useMemo<boolean>(() => {
    return !!userProfile?.isAdmin;
  }, [userProfile]);

  useLayout({
    fixedHeader: (
      <>
        <MainHead
          backButton={{ isShow: true, directlyPassedURL: pageInfo.productListPage.path }}
          headerTitle={isAdmin && product ? product.code : ''}
        />
        <InstallmentPossibleSection />
      </>
    ),
    fixedFooter: (
      <ProductDetailPageFooter>
        <RequestConsultButton product={product!} category={product?.category === 'PLUVA' ? 'PLUVA' : 'PARTNER'} />
      </ProductDetailPageFooter>
    ),
    fixedHeaderHeight: MAIN_HEAD_HEIGHT + INSTALLMENT_POSSIBLE_SECTION_HEIGHT,
    fixedFooterHeight: 0,
  });

  if (isGetProductQueryLoading) return <AnimateLoading />;

  if (product) {
    return (
      <>
        <Helmet>
          <title>{`${product.title} - Pluva Market`}</title>
        </Helmet>

        <ProductDetailView product={product} category={product.category === 'PLUVA' ? 'PLUVA' : 'PARTNER'} />
      </>
    );
  }

  return <></>;
};
